import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Select, MenuItem, ListItemText, Paper, Tab } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
// cmps
import CenterCircularProgress from "../cmp/CenterCircularProgress";
import GatewayStatChart from "../cmp/gateway/GatewayStatChart";
import GatewayDeviceStatChart from "../cmp/gateway/GatewayDeviceStatChart";
import BiDashboards from "../cmp/BiDashboards";
// contexts
import { useUserData } from "../context/UserDataContext";
import { useServerOption } from "../context/ServerOptionContext";
// hooks
import useSend from "../hooks/useSend";
// services
import Gupport from "../services/gupport";
import Responsive from "../services/responsive";
// types
import type { ReadonlyDeep, ValueOf } from "type-fest";
import type { SelectChangeEvent } from "@mui/material/Select";
import type { GetGatewayStatsData, /*CmdGetDeviceStats,*/ CmdGetGatewayStats, CmdGetTable, GatewayBucketData, DeviceBucketData, GetDeviceStatsData } from "../types/gupport";
import type { RocIdData, RocIdTable } from "../types/roc-table";

const TAB_IDS = {
	STATS: "stats",
	ANALYTICS: "analytics",
} as const;

type TabId = ValueOf<typeof TAB_IDS>;

const GRID_SCREEN_COLUMNS = {
	md: true,
	sm: false,
	xs: false,
} as const;

const WelcomePage = () => {
	const send = useSend();
	const { t } = useTranslation();
	const navigate = useNavigate();

	const serverOption = useServerOption();
	const { ready } = useUserData();

	const [selectedTab, setSelectedTab] = useState<TabId>(TAB_IDS.STATS);

	const [loading, setLoading] = useState(true);
	const [container, setContainer] = useState(Boolean(Responsive.getScreenSizes().find((screen) => (GRID_SCREEN_COLUMNS[screen])) ?? GRID_SCREEN_COLUMNS.xs));
	const [rocIdTableData, setRocIdTableData] = useState<ReadonlyArray<RocIdData> | undefined>(undefined);
	const [deviceStatData, setDeviceStatData] = useState<ReadonlyDeep<GetDeviceStatsData> | undefined>(undefined); // eslint-disable-line @typescript-eslint/no-unused-vars
	const [selectedDeviceStat, setSelectedDeviceStat] = useState<ReadonlyDeep<DeviceBucketData> | undefined>(undefined);
	const [gatewayStatData, setGatewayStatData] = useState<ReadonlyDeep<GetGatewayStatsData> | undefined>(undefined);
	const [selectedGatewayStat, setSelectedGatewayStat] = useState<ReadonlyDeep<GatewayBucketData> | undefined>(undefined);

	useEffect(() => {
		if (selectedGatewayStat && deviceStatData) {
			const selectedBucket = deviceStatData.channel.buckets.find((bucket) => (bucket.val === selectedGatewayStat.val));
			if (selectedBucket) {
				setSelectedDeviceStat(selectedBucket);
			}
		}
	}, [selectedGatewayStat, deviceStatData]);

	useEffect(() => {
		const handleScreenChanged = () => {
			setContainer(Boolean(Responsive.getScreenSizes().find((screen) => (GRID_SCREEN_COLUMNS[screen])) ?? GRID_SCREEN_COLUMNS.xs));
		};

		Responsive.on("screenChanged", handleScreenChanged);

		if (ready && Gupport.getGatewayStats) {
			const tasks = [
				new Promise<void>((resolve, reject) => {
					const cmd = {
						action: "getTable",
						tableName: "rocid_dict",
					} as const satisfies CmdGetTable;
					send(cmd, (error, msg) => {
						if (!error && msg?.payload.status === "ok") {
							setRocIdTableData((msg.payload.data as ReadonlyDeep<RocIdTable>).map((entry) => (entry.data)));
							resolve();
						} else {
							reject(error);
						}
					});
				}),
				new Promise<void>((resolve, reject) => {
					const cmd = {
						action: "getGatewayStats",
					} as const satisfies CmdGetGatewayStats;
					send(cmd, (error, msg) => {
						if (!error && msg?.payload.status === "ok") {
							setGatewayStatData(msg.payload.data);
							setSelectedGatewayStat(msg.payload.data.channel.buckets[0]);
							resolve();
						} else {
							reject(error);
						}
					});
				}),
				// TODO: currently not implemented on backend
				// new Promise<void>((resolve, reject) => {
				// 	const cmd = {
				// 		action: "getDeviceStats",
				// 	} as const satisfies CmdGetDeviceStats;
				// 	send(cmd, (error, msg) => {
				// 		if (!error && msg?.payload.status === "ok") {
				// 			setDeviceStatData(msg.payload.data);
				// 			setSelectedDeviceStat(undefined);
				// 			resolve();
				// 		} else {
				// 			reject(error);
				// 		}
				// 	});
				// }),
			];

			void (async () => {
				try {
					await Promise.all(tasks);
				} catch (_error) {
					console.warn("Stats loading failed", _error);
				} finally {
					setLoading(false);
				}
			})();
		} else {
			// TODO: check async navigate
			void navigate("/gateways");
		}

		return () => {
			Responsive.off("screenChanged", handleScreenChanged);
		};
	}, [ready, navigate, send]);

	if (loading) {
		return <CenterCircularProgress />;
	}

	const handleSelect = (event: SelectChangeEvent) => {
		const currentSelectedGatewayStat = gatewayStatData?.channel.buckets.find((bucket) => (bucket.val === event.target.value));
		const currentSelectedDeviceStat = deviceStatData?.channel.buckets.find((bucket) => (bucket.val === event.target.value));

		setSelectedGatewayStat(currentSelectedGatewayStat);
		setSelectedDeviceStat(currentSelectedDeviceStat);
	};

	const isDevServer = serverOption.id === "dev";

	return (
		<TabContext value={selectedTab}>
			{isDevServer &&
				<Paper component="section" style={{ marginBottom: "12px" }}>
					<TabList
						aria-label={`${t("welcome.stats")} / ${t("welcome.analytics")}`}
						onChange={(_event, value: TabId) => (setSelectedTab(value))}
					>
						<Tab
							id={`tab-welcome-${TAB_IDS.STATS}`}
							aria-controls={`tabpanel-welcome-${TAB_IDS.STATS}`}
							label={t("welcome.stats")}
							value={TAB_IDS.STATS}
						/>
						<Tab
							id={`tab-welcome-${TAB_IDS.ANALYTICS}`}
							aria-controls={`tabpanel-welcome-${TAB_IDS.ANALYTICS}`}
							label={t("welcome.analytics")}
							value={TAB_IDS.ANALYTICS}
						/>
					</TabList>
				</Paper>
			}
			<TabPanel
				id={`tabpanel-welcome-${TAB_IDS.STATS}`}
				aria-labelledby={`tab-welcome-${TAB_IDS.STATS}`}
				value={TAB_IDS.STATS}
				style={{ padding: 0 }}
			>
				<div className="page-header-padding">
					{(selectedGatewayStat && gatewayStatData && gatewayStatData.channel.buckets.length > 1) &&
						<Select
							name="channel"
							value={selectedGatewayStat.val}
							style={{ width: "45%" }}
							onChange={handleSelect}
						>
							{gatewayStatData.channel.buckets.map((bucket, index) => (
								<MenuItem key={index} value={bucket.val}>
									<ListItemText primary={bucket.val} />
								</MenuItem>
							))}
						</Select>
					}
					<div>
						{selectedGatewayStat && <GatewayStatChart container={container} selectedGatewayStat={selectedGatewayStat} />}
						{selectedDeviceStat && <GatewayDeviceStatChart container={container} selectedDeviceStat={selectedDeviceStat} rocIdTableData={rocIdTableData} />}
						<br style={{ clear: "both" }} />
					</div>
				</div>
			</TabPanel>
			{isDevServer &&
				<TabPanel
					id={`tabpanel-welcome-${TAB_IDS.ANALYTICS}`}
					aria-labelledby={`tab-welcome-${TAB_IDS.ANALYTICS}`}
					value={TAB_IDS.ANALYTICS}
					style={{ padding: 0 }}
				>
					<BiDashboards />
				</TabPanel>
			}
		</TabContext>
	);
};

export default WelcomePage;
